import { formatTripCounter } from "../../services/tripDisplay";
import { tripDisplayStart } from "../../services/tripDisplayDuration";
import React from "react";
import Tooltip from "@mui/material/Tooltip";
import Popper from "@mui/material/Popper";
import "./ManageTripTimeline.css";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { Dialog, Loader } from "../utils";
import FilePreview from "../utils/FilePreview";
import { useGetDockTripOperationsQuery } from "../../api/vmsSlice";
import { useDockChangeRefetch } from "./useDockRefresh";
import { lifecycleBadgeClass, lifecycleBadgeLabel, lifecycleSummary, formatPlantTime } from "./dockLifecycle";

const UNDETECTED_GATE = "Gate In not detected";
const isLoadingEvent = item => item?.eventType === "LOADING" || item?.eventType === "UNLOADING";
const operationTimes = (item, timezone) => `Start: ${formatEventTime(item.eventAt, timezone)} · End: ${Number.isFinite(timestamp(item.eventEnd)) ? formatEventTime(item.eventEnd, timezone) : "Not recorded"}`;
const shownTime = (item, value, timezone) => item?.hideFromUi ? UNDETECTED_GATE : formatEventTime(value, timezone);

const records = value => Array.isArray(value) ? value.filter(item => item && typeof item === "object" && !Array.isArray(item)) : [];
const displayValue = value => typeof value === "string" || (typeof value === "number" && Number.isFinite(value)) ? value : "";
function timestamp(value) {
	if ((typeof value !== "string" && typeof value !== "number") || value === "") return NaN;
	return new Date(value).getTime();
}
function normalizeOperations(value) {
	if (!value || typeof value !== "object" || Array.isArray(value)) return null;
	const source = value => ({ origin: String(displayValue(value?.origin)) });
	const event = (value, index) => ({
		...value, _id: `${displayValue(value._id) || "event"}-${index}`,
		eventType: displayValue(value.eventType) || "Event", checkpointNumber: displayValue(value.checkpointNumber) || "—",
		eventAt: timestamp(value.eventAt), eventEnd: timestamp(Number.isFinite(timestamp(value.eventEnd)) ? value.eventEnd : value.endedAt), source: source(value.source),
		observation: { quantity: displayValue(value.observation?.quantity) === "" ? null : value.observation.quantity, unit: displayValue(value.observation?.unit), idle: value.observation?.idle },
	});
	const trip = value.trip || {};
	return {
		trip: { ...trip, inTime: timestamp(trip.inTime), outTime: timestamp(trip.outTime), modifiedOn: timestamp(trip.modifiedOn), tripStatus: displayValue(trip.tripStatus), allocatedBay: displayValue(trip.allocatedBay), dockLifecycle: trip.dockLifecycle || null },
		movements: records(value.movements).map((movement, index) => ({
			...movement, _id: `${displayValue(movement._id) || "movement"}-${index}`,
			inTime: timestamp(movement.inTime), outTime: timestamp(movement.outTime),
			displayName: displayValue(movement.displayName), checkpointNumber: displayValue(movement.checkpointNumber) || "—",
			checkpointType: String(displayValue(movement.checkpointType)).toLowerCase(),
			source: source(movement.source || { origin: movement.type }), events: records(movement.events).map(event),
		})),
		ungroupedEvents: records(value.ungroupedEvents).map(event),
		liveStates: records(value.liveStates).map(state => ({ state: displayValue(state.state) })),
	};
}

// Portal the details outside scrolling drawers so the ribbon cannot clip them.
function RibbonSegment({ children, inline = false, ...props }) {
	const [label, details] = React.Children.toArray(children);
	if (inline) return <div {...props}>
		{label}
		{React.cloneElement(details, { className: "dock-rail-details" })}
	</div>;
	return (
		<Tooltip
			title={React.cloneElement(details, { className: "dock-ribbon-tooltip-content" })}
			placement="bottom"
			describeChild
			componentsProps={{ tooltip: { sx: {
				maxWidth: 280,
				padding: "10px 12px",
				border: "1px solid var(--input-border-color)",
				borderRadius: "8px",
				color: "var(--text-color)",
				backgroundColor: "var(--generic-surface-bg-color, #fff)",
				boxShadow: "0 6px 22px rgba(0, 0, 0, 0.16)",
			} } }}
		>
			<button type="button" {...props}>{label}</button>
		</Tooltip>
	);
}
function RailSource({ source }) {
	if (!source?.origin) return null;
	return <span className="dock-rail-source" aria-label={`Source: ${source.origin}`}>
		{source.origin.toLowerCase() === "manual" ? <PersonOutlineIcon /> : /camera/i.test(source.origin) ? <CameraAltOutlinedIcon /> : null}
		{milestoneLabel(source.origin)}
	</span>;
}

function hasBayAssignmentMismatch(segment, trip) {
	return segment.isFirstBayMovement && segment.checkpointId && trip?.allocatedBayCheckpointId && trip?.allocatedBay
		&& segment.checkpointId !== trip.allocatedBayCheckpointId;
}

function EvidencePreview({ imagePath, onClose }) {
	return <Dialog size="md" open={Boolean(imagePath)} handleClose={onClose} noBtn title="ANPR Evidence" minHeight="60vh">
			{imagePath && <div style={{ minHeight: "50vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
				<FilePreview key={imagePath} fileKey={imagePath} extension={String(imagePath).split(".").pop()} inline fallbackText="No image preview" />
			</div>}
		</Dialog>;
}

function EvidenceRecorded({ evidence }) {
	const [open, setOpen] = React.useState(false);
	const imagePath = evidence?.anprImagePath;
	if (!imagePath) return null;
	return <>
		<button type="button" className="dock-rail-extra" onClick={() => setOpen(true)}
			style={{ background: "none", border: 0, padding: 0, cursor: "pointer", textDecoration: "underline" }}>
			<CameraAltOutlinedIcon /> Evidence recorded
		</button>
		<EvidencePreview imagePath={open ? imagePath : null} onClose={() => setOpen(false)} />
	</>;
}

function RailStage({ segment, trip, timezone, index, active }) {
	const duration = Math.max(0, segment.to - segment.from);
	const events = withBayBoundaries(segment, (segment.events || []).map(event => ({ ...event, label: (event.eventType === "UNLOADING" || event.eventType === "LOADING" ? "LOADING/ UNLOADING" : String(event.eventType).replaceAll("_", " ")) })), true);
	return <div className={`dock-visit-group dock-visit-${segment.kind}${active ? " dock-rail-active" : ""}`} style={{ "--rail-delay": `${Math.min(index, 8) * 140}ms` }}>
		<span className="dock-rail-node" aria-hidden="true">{segment.kind === "transit" && <LocalShippingIcon />}</span>
		<div className="dock-rail-heading">
			<strong>{segment.label}{hasBayAssignmentMismatch(segment, trip) && <span className="dock-rail-assignment"> (Assigned to {trip.allocatedBay})</span>}</strong>
			{segment.eventAt == null && duration > 0 && <span className={`dock-rail-duration ${segment.kind}`} aria-label={`Duration: ${formatDuration(duration)}`}>{formatDuration(duration)}</span>}
			<RailSource source={segment.source} />
		</div>
		<div className={`dock-rail-time${segment.eventAt != null && segment.hideFromUi ? " dock-gate-undetected" : ""}`}>{segment.eventAt != null ? shownTime(segment, segment.eventAt, timezone) : <>{formatEventTime(segment.from, timezone)} <span aria-label="to">→</span> {formatEventTime(segment.to, timezone)}</>}</div>
		{segment.location !== segment.label && segment.kind !== "transit" && <div className="dock-rail-extra">Location · {segment.location}</div>}
		{segment.observation?.quantity != null && <div className="dock-rail-extra">Quantity · {segment.observation.quantity} {segment.observation.unit || "units"}</div>}
		<EvidenceRecorded evidence={segment.evidence} />
		{events.length > 0 && <div className="dock-visit-events">
			{events.map((event, index) => {
				const interval = isActivityInterval(event);
				const endTime = Number.isFinite(timestamp(event.eventEnd)) ? event.eventEnd : event.endedAt;
				const showEndTime = interval || (
					(event.eventType === "LOADING" || event.eventType === "UNLOADING")
					&& Number.isFinite(timestamp(endTime))
				);
				const next = events[index + 1];
				const connected = interval && isActivityInterval(next || {}) && new Date(event.eventEnd).getTime() === new Date(next.eventAt).getTime();
				return <div className={`dock-visit-event${interval ? ` dock-rail-interval ${event.observation.idle ? "idle" : "active"}${connected ? " connected" : ""}` : ""}`} key={event._id || index}>
				{interval && <span className="dock-rail-activity-bar" aria-hidden="true" />}
				<div className="dock-rail-heading"><strong>{event.label}</strong><RailSource source={event.source} /></div>
				<div className="dock-rail-time">{formatEventTime(event.eventAt, timezone)}{showEndTime && <> → {formatEventTime(endTime, timezone)}</>}</div>
				{interval && <div className="dock-rail-interval-status">{event.observation.idle ? "Idle zone" : "Active"} · {formatDuration(new Date(event.eventEnd) - new Date(event.eventAt))}</div>}
				{event.observation?.quantity != null && <div className="dock-rail-extra">Quantity · {event.observation.quantity} {event.observation.unit || "units"}</div>}
				<EvidenceRecorded evidence={event.evidence} />
			</div>;
			})}
		</div>}
	</div>;
}

function VisitEvents({ events, location, timezone, inline = false }) {
	if (!events?.length) return null;
	return <div className="dock-visit-events" aria-label={`${location} events`}>
		{events.map((event, index) => <RibbonSegment
			inline={inline}
			key={event._id || index}
			
			className="dock-visit-event"
			aria-label={`${(event.eventType === "UNLOADING" || event.eventType === "LOADING" ? "LOADING /UNLOADING" : String(event.eventType).replaceAll("_", " "))} at ${location}, ${formatEventTime(event.eventAt, timezone)}`}
		>
			<span>{(event.eventType === "UNLOADING" || event.eventType === "LOADING" ? "LOADING /UNLOADING" : String(event.eventType).replaceAll("_", " "))}</span>
			<span>
				<strong>{(event.eventType === "UNLOADING" || event.eventType === "LOADING" ? "LOADING /UNLOADING" : String(event.eventType).replaceAll("_", " "))}</strong>
				<span><em>Location</em><b>{location}</b></span>
				<span><em>Time</em><b>{formatEventTime(event.eventAt, timezone)}</b></span>
				{event.observation?.quantity != null && <span><em>Quantity</em><b>{event.observation.quantity} {event.observation.unit || "units"}</b></span>}
				{event.source?.origin && <span><em>Source</em><b>{event.source.origin}</b></span>}
				{event.evidence && <span><em>Evidence</em><b><CameraAltOutlinedIcon /> Recorded</b></span>}
			</span>
		</RibbonSegment>)}
	</div>;
}

// Formats operational timestamps in the plant timezone.
function formatEventTime(value, timezone) {
	const time = timestamp(value);
	if (!Number.isFinite(time)) return "—";
	const options = {
		day: "2-digit",
		month: "short",
		hour: "2-digit",
		minute: "2-digit",
		second: "2-digit",
	};
	try {
		return new Intl.DateTimeFormat("en-MY", { ...options, timeZone: timezone || "UTC" }).format(time);
	} catch {
		return new Intl.DateTimeFormat("en-MY", { ...options, timeZone: "UTC" }).format(time);
	}
}

// Formats a ribbon section duration without hiding short operational events.
function formatDuration(milliseconds) {
	if (!Number.isFinite(milliseconds)) return "—";
	const seconds = Math.max(0, Math.round(milliseconds / 1000));
	const hours = Math.floor(seconds / 3600);
	const minutes = Math.floor((seconds % 3600) / 60);
	const remainder = seconds % 60;
	return `${hours ? `${hours}h ` : ""}${hours || minutes ? `${minutes}m ` : ""}${remainder}s`;
}

// Converts normalized milestone names to readable labels.
function milestoneLabel(value) {
	if (value === "UNLOADING" || value === "LOADING") return "LOADING/ UNLOADING";
	return String(value || "Checkpoint visit").replaceAll("_", " ").toLowerCase().replace(/(^|\s)\S/g, (letter) => letter.toUpperCase());
}

// Assigns each section to one stable ribbon category.
function getSegmentKind(checkpointType, eventType) {
	if (checkpointType === "transit") return "transit";
	if (checkpointType === "queue") return "queue";
	if (String(checkpointType || "").toLowerCase().includes("gate")) return "gate";
	if (/DOOR|LOADING|UNLOADING|UNITS/.test(String(eventType || "").toUpperCase())) return "operation";
	return "bay";
}

// Converts complete movement history and milestones into chronological ribbon sections.
function buildTripSegments(operations) {
	const firstBayMovement = operations?.movements?.find(movement => movement.checkpointType === "bay");
	const movements = [...(operations?.movements || [])].filter((movement) => Number.isFinite(movement.inTime))
		.sort((first, second) => new Date(first.inTime) - new Date(second.inTime));
	const tripEnd = Number.isFinite(operations?.trip?.outTime)
		? new Date(operations.trip.outTime).getTime()
		: operations?.trip?.tripOver && Number.isFinite(operations.trip.modifiedOn)
			? new Date(operations.trip.modifiedOn).getTime()
			: Date.now();
	const segments = [];
	let previousEnd = null;
	let previousTimeHidden = false;

	movements.forEach((movement, movementIndex) => {
		const start = new Date(movement.inTime).getTime();
		const nextMovement = movements[movementIndex + 1];
		const endCandidates = [
			movement.outTime ? new Date(movement.outTime).getTime() : null,
			nextMovement?.inTime ? new Date(nextMovement.inTime).getTime() : null,
			tripEnd,
		].filter((value) => Number.isFinite(value) && value >= start);
		const validOutTime = Number.isFinite(movement.outTime) && movement.outTime >= start;
		const end = validOutTime ? movement.outTime : (endCandidates.length ? Math.min(...endCandidates) : start);

		if (!previousTimeHidden && previousEnd !== null && start > previousEnd) {
			segments.push({
				key: `transit-${movementIndex}`,
				kind: "transit",
				label: `Transit to ${movement.displayName || `Checkpoint ${movement.checkpointNumber}`}`,
				location: movement.displayName || `Checkpoint ${movement.checkpointNumber}`,
				eventAt: null,
				from: previousEnd,
				to: start,
			});
		}

		const events = [...(movement.events || [])].filter((event) => Number.isFinite(new Date(event.eventAt).getTime()))
			.sort((first, second) => new Date(first.eventAt) - new Date(second.eventAt));
		// Keep the full checkpoint visit; its events belong beneath it, not in place of it.
		const kind = getSegmentKind(movement.checkpointType);
		const hideFromUi = movement.hideFromUi === true && movement.checkpointType === "gatein";
		const instantGate = kind === "gate" && (hideFromUi || end - start < 1000);
		segments.push({
			key: `visit-${movement._id || movementIndex}`,
			checkpointId: movement.checkpointId,
			isFirstBayMovement: movement === firstBayMovement,
			hideFromUi,
			kind,
			label: movement.displayName || (hideFromUi ? "Gate In" : `Checkpoint ${movement.checkpointNumber}`),
			location: movement.displayName || `Checkpoint ${movement.checkpointNumber}`,
			checkpointNumber: movement.checkpointNumber,
			eventAt: instantGate ? start : null,
			from: start,
			outTime: validOutTime ? movement.outTime : null,
			to: end,
			source: movement.source || (movement.type ? { origin: movement.type } : null),
			events,
		});
		previousEnd = Math.max(previousEnd ?? start, end);
		previousTimeHidden = hideFromUi;
	});

	for (const event of operations?.ungroupedEvents || []) {
		if (!Number.isFinite(new Date(event.eventAt).getTime())) continue;
		const time = new Date(event.eventAt).getTime();
		segments.push({
			key: `ungrouped-${event._id}`,
			eventType: event.eventType,
			kind: getSegmentKind("", event.eventType),
			label: milestoneLabel(event.eventType),
			location: `Checkpoint ${event.checkpointNumber}`,
			checkpointNumber: event.checkpointNumber,
			eventAt: time,
			from: time,
			to: isActivityInterval(event) ? event.eventEnd : time,
			observation: event.observation,
			eventEnd: event.eventEnd,
			events: isActivityInterval(event) ? [event] : [],
			source: event.source,
			evidence: event.evidence,
		});
	}

	if (operations?.trip?.tripOver && operations.trip.tripStatus === "Terminated" && Number.isFinite(operations.trip.modifiedOn)) {
		const terminatedAt = new Date(operations.trip.modifiedOn).getTime();
		segments.push({
			key: "trip-terminated",
			kind: "operation",
			label: "Trip Terminated",
			location: "Trip",
			eventAt: terminatedAt,
			from: terminatedAt,
			to: terminatedAt,
		});
	}

	return segments.sort((first, second) => first.from - second.from);
}

function isActivityInterval(item) {
	return typeof item?.observation?.idle === "boolean"
		&& item.eventAt != null && item.eventEnd != null
		&& Number.isFinite(new Date(item.eventAt).getTime())
		&& new Date(item.eventEnd).getTime() > new Date(item.eventAt).getTime();
}

// Keep visit boundaries separate when requested, even when their timestamps coincide.
function withBayBoundaries(segment, items, separateBoundaries = false) {
	if (segment.kind !== "bay") return items;
	if (separateBoundaries) {
		const boundary = (name, time) => ({
			_id: `${segment.key}-bay-${name}`, label: `Bay ${name}`, eventAt: time, location: segment.location, bayBoundary: name,
		});
		return [
			boundary("in", segment.from),
			...items,
			...(segment.outTime != null ? [boundary("out", segment.outTime)] : []),
		];
	}
	for (const [boundary, time] of [["In", segment.from], ["Out", segment.outTime]]) {
		if (time == null) continue;
		const matching = items.find(item => !isActivityInterval(item) && new Date(item.eventAt).getTime() === new Date(time).getTime());
		if (matching) matching.label += ` · ${boundary}`;
		else items.push({ _id: `${segment.key}-bay-${boundary.toLowerCase()}`, label: `Bay ${boundary.toLowerCase()}`, eventAt: time, location: segment.location });
	}
	return items.sort((a, b) => new Date(a.eventAt) - new Date(b.eventAt));
}

// Share positions for identical timestamps so adjacent intervals meet exactly.
// Minimum spacing keeps short milestones readable on the scrollable track.
function activityLayout(items, segment) {
	if (!items.some(isActivityInterval)) return null;
	const times = [...new Set([segment.from, segment.to, ...items.flatMap(item => [
		new Date(item.eventAt).getTime(),
		...(isActivityInterval(item) ? [new Date(item.eventEnd).getTime()] : []),
	])].filter(Number.isFinite))].sort((a, b) => a - b);
	const duration = times[times.length - 1] - times[0];
	const hasBoundaries = items.some(item => item.bayBoundary);
	const positions = new Map([[times[0], hasBoundaries ? 210 : 70]]);
	let position = hasBoundaries ? 210 : 70;
	for (let index = 1; index < times.length; index++) {
		position += Math.max(240, (times[index] - times[index - 1]) / duration * items.length * 140);
		positions.set(times[index], position);
	}
	return { positions, width: position + (hasBoundaries ? 210 : 70) };
}

function ManageTripTimeline({ segments, operations, timezone }) {
	const [selection, setSelection] = React.useState(null);
	const [evidenceImage, setEvidenceImage] = React.useState(null);
	const closeTimer = React.useRef(null);
	const keepOpen = () => clearTimeout(closeTimer.current);
	const closeDetails = () => { keepOpen(); closeTimer.current = setTimeout(() => setSelection(null), 150); };
	const showDetails = (event, item) => { keepOpen(); setSelection({ anchor: event.currentTarget, item }); };
	React.useEffect(() => () => clearTimeout(closeTimer.current), []);
	React.useEffect(() => { setSelection(null); }, [operations]);
	const trip = operations.trip;
	// The trip-level times can describe the latest checkpoint. Summarize the
	// full timeline instead, including the first gate/queue arrival.
	const first = segments.reduce((value, segment) => Number.isFinite(segment.from) ? Math.min(value, segment.from) : value, Infinity);
	const last = segments.reduce((value, segment) => Number.isFinite(segment.to) ? Math.max(value, segment.to) : value, -Infinity);
	const displayStart = tripDisplayStart(operations.movements);
	const start = displayStart ?? (Number.isFinite(first) ? first : trip?.inTime);
	const completed = Boolean(trip?.tripOver) || Number.isFinite(trip?.outTime);
	const end = completed
		? Number.isFinite(last) ? last : Number.isFinite(trip?.outTime) ? trip.outTime : null
		: null;
	const until = end ?? (completed ? NaN : Date.now());
	const elapsed = Number.isFinite(start) && Number.isFinite(until) && until >= start ? until - start : NaN;
	let milestoneIndex = 0;
	const selected = selection?.item;
	return <section className="manage-timeline" aria-label="Trip timeline">
		<div className="manage-timeline-heading">
			<h5>Trip Timeline</h5>
			<div className="manage-timeline-summary">
				{trip.allocatedBay !== "" && <span>Allocated bay: <strong>{trip.allocatedBay}</strong></span>}
				<span>Trip start: <strong className={segments[0]?.hideFromUi ? "dock-gate-undetected" : undefined}>{shownTime(segments[0], start, timezone)}</strong></span>
				<span>Trip end: <strong>{end != null ? formatEventTime(end, timezone) : completed ? "Not recorded" : "In progress"}</strong></span>
				{/* <span>Total: <strong>{formatDuration(elapsed)}</strong></span> */}
			</div>
		</div>
		{!segments.length ? <div className="dock-empty">No trip operations were recorded.</div> : <>
		<div className="manage-timeline-scroll" tabIndex={0} role="region" aria-label="Scrollable trip milestones">
			<div className="manage-timeline-track">
				{segments.map(segment => {
					const transit = segment.kind === "transit";
					const showAllocation = hasBayAssignmentMismatch(segment, trip);
					const eventItems = segment.events?.length ? segment.events.map(event => ({ ...event, hideFromUi: segment.hideFromUi, label: milestoneLabel(event.eventType), location: segment.location })) : segment.kind === "bay" ? [] : [{ ...segment, label: transit ? "Transit" : segment.label, eventAt: segment.eventAt ?? segment.from }];
					const items = withBayBoundaries(segment, eventItems, true);
					const activity = activityLayout(items, segment);
					return <div key={segment.key} className={`manage-timeline-group ${segment.kind}`} style={{ "--stage-width": `${activity?.width || (transit ? 120 : Math.max(segment.kind === "bay" ? 280 : 140, items.length * 140))}px` }}>
						<button type="button" className="manage-timeline-band" onMouseEnter={event => showDetails(event, segment)} onMouseLeave={closeDetails} onFocus={event => showDetails(event, segment)} onBlur={closeDetails} onKeyDown={event => { if (event.key === "Escape") setSelection(null); }}>
							<strong>{transit ? "Transit" : segment.label}{showAllocation && <span className="manage-timeline-allocation"> (Assigned to {trip.allocatedBay})</span>}</strong>
							{segment.eventAt == null && <span className="manage-timeline-stage-duration">{formatDuration(segment.to - segment.from)}</span>}
						</button>
						<div className="manage-timeline-events">
							{items.map((item, index) => {
								const interval = isActivityInterval(item);
								const status = item.observation?.idle ? "Idle zone" : "Active";
								const left = activity && item.bayBoundary ? (item.bayBoundary === "in" ? 70 : activity.width - 70) : activity?.positions.get(new Date(item.eventAt).getTime());
								const right = interval ? activity.positions.get(new Date(item.eventEnd).getTime()) : left;
								const style = activity ? { left: `${(interval ? left : left - 70) / activity.width * 100}%`, width: `${(interval ? right - left : 140) / activity.width * 100}%` } : undefined;
								return <div tabIndex={0} key={item._id || index} style={style} className={`manage-timeline-event ${activity ? "positioned" : ""} ${interval ? `activity-interval ${item.observation.idle ? "idle" : "active"}` : ""} ${milestoneIndex++ % 2 ? "below" : "above"}`} aria-label={`${item.label}${isLoadingEvent(item) ? `, ${operationTimes(item, timezone)}` : interval ? `, ${status}, ${formatEventTime(item.eventAt, timezone)} to ${formatEventTime(item.eventEnd, timezone)}` : `, ${shownTime(item, item.eventAt, timezone)}`}`} onMouseEnter={event => showDetails(event, item)} onMouseLeave={closeDetails} onFocus={event => showDetails(event, item)} onBlur={closeDetails} onKeyDown={event => { if (event.key === "Escape") setSelection(null); }}>
									<span className={interval ? "manage-timeline-activity-line" : "manage-timeline-dot"} aria-hidden="true" />
									<span className="manage-timeline-event-label"><strong>{item.label}{item.evidence?.anprImagePath && <button type="button" className="manage-timeline-evidence" aria-label={`View ANPR evidence for ${item.label}`} title="View ANPR evidence" onClick={event => { event.stopPropagation(); keepOpen(); setSelection(null); setEvidenceImage(item.evidence.anprImagePath); }}><CameraAltOutlinedIcon /></button>}</strong>{interval && <span className="manage-timeline-event-status">{status}</span>}{isLoadingEvent(item) ? <span className="manage-timeline-event-times"><span>Start: {formatEventTime(item.eventAt, timezone)}</span><span>End: {Number.isFinite(timestamp(item.eventEnd)) ? formatEventTime(item.eventEnd, timezone) : "Not recorded"}</span></span> : <span>{interval ? formatDuration(new Date(item.eventEnd) - new Date(item.eventAt)) : shownTime(item, item.eventAt, timezone)}</span>}</span>
								</div>;
							})}
						</div>
					</div>;
				})}
			</div>
		</div>
		</>}
		<Popper open={Boolean(selection)} anchorEl={selection?.anchor} placement="bottom" style={{ zIndex: 1500 }} modifiers={[{ name: "offset", options: { offset: [0, 6] } }]}>
		<div className="manage-timeline-popup" onMouseEnter={keepOpen} onMouseLeave={closeDetails} onFocus={keepOpen} onBlur={closeDetails} onKeyDown={event => { if (event.key === "Escape") setSelection(null); }}>
			{selected && <>
				<div className="manage-timeline-popup-heading"><strong>{selected.label}</strong><button type="button" onClick={() => setSelection(null)} aria-label="Close milestone details">×</button></div>
				<dl>
					<dt>Location</dt><dd>{selected.location}</dd>
					{isLoadingEvent(selected) ? <><dt>Start time</dt><dd>{formatEventTime(selected.eventAt, timezone)}</dd><dt>End time</dt><dd>{Number.isFinite(timestamp(selected.eventEnd)) ? formatEventTime(selected.eventEnd, timezone) : "Not recorded"}</dd></> : selected.hideFromUi ? <><dt>Gate In</dt><dd className="dock-gate-undetected">{UNDETECTED_GATE}</dd></> : selected.eventAt != null ? <><dt>Recorded at</dt><dd>{formatEventTime(selected.eventAt, timezone)}</dd></> : <>
						<dt>In</dt><dd>{formatEventTime(selected.from, timezone)}</dd>
						<dt>Out</dt><dd>{selected.kind === "transit" ? formatEventTime(selected.to, timezone) : selected.outTime ? formatEventTime(selected.outTime, timezone) : "Not recorded"}</dd>
						<dt>Duration</dt><dd>{formatDuration(selected.to - selected.from)}</dd>
					</>}
					{selected.observation?.quantity != null && <><dt>Quantity</dt><dd>{selected.observation.quantity} {selected.observation.unit || "units"}</dd></>}
					{isActivityInterval(selected) && <>
						{!isLoadingEvent(selected) && <><dt>End</dt><dd>{formatEventTime(selected.eventEnd, timezone)}</dd></>}
						<dt>Duration</dt><dd>{formatDuration(new Date(selected.eventEnd) - new Date(selected.eventAt))}</dd>
						<dt>Status</dt><dd>{selected.observation.idle ? "Idle zone" : "Active"}</dd>
					</>}
					{selected.source?.origin && <><dt>Source</dt><dd>{milestoneLabel(selected.source.origin)}</dd></>}
					{selected.evidence && <><dt>Evidence</dt><dd>Recorded</dd></>}
				</dl>
			</>}
		</div>
		</Popper>
		<EvidencePreview imagePath={evidenceImage} onClose={() => setEvidenceImage(null)} />
	</section>;
}

// Preserves the existing drawer layouts alongside the Manage Trip timeline.
export default function DockTripOperations({ tripId, plantId, refreshSignal, layout = "ribbon" }) {
	const query = useGetDockTripOperationsQuery({ tripId, pagesize: 1000, page_no: 1 }, { skip: !tripId, refetchOnMountOrArgChange: true });
	const { refetch } = query;
	useDockChangeRefetch(tripId && !query.isUninitialized ? plantId : null, refetch);
	React.useEffect(() => {
		if (refreshSignal && tripId && !query.isUninitialized) refetch();
	}, [refreshSignal, refetch, tripId, query.isUninitialized]);
	const response = query.data;
	const operations = React.useMemo(() => normalizeOperations(response?.data), [response?.data]);
	const segments = buildTripSegments(operations);
	const timelineEnd = segments.reduce((latest, segment) => Math.max(latest, segment.to), -Infinity);
	const displayStart = tripDisplayStart(operations?.movements || []);
	const totalDuration = segments.length && displayStart != null ? Math.max(0, timelineEnd - displayStart) : 0;
	const latestState = operations?.liveStates?.[0];
	const kinds = [
		{ key: "queue", label: "Queue" },
		{ key: "gate", label: "Gate" },
		{ key: "transit", label: "Transit" },
		{ key: "bay", label: "Bay movement" },
		{ key: "operation", label: "Dock operation" },
	].filter((kind) => segments.some((segment) => segment.kind === kind.key || (kind.key === "operation" && segment.events?.length)));

	if (query.isLoading) return <Loader size="2rem" />;
	if (query.error || response?.error) return <div className="dock-message error">{displayValue(response?.message) || "Trip operations could not be loaded."}</div>;
	if (!operations) return null;
	if (layout === "manage") return <ManageTripTimeline key={tripId} segments={segments} operations={operations} timezone={response.timezone} />;

	return (
		<section className={`dock-trip-operations${layout === "timeline" ? " dock-trip-vertical" : layout === "vertical" ? " dock-ribbon-vertical" : ""}`}>
			<div className="dock-trip-title statusHistory-detail-header">
					<div>
						<h5>Trip Timeline</h5>
						{displayValue(operations.trip?.vehicleNumber) !== "" && (
							<div className="dock-trip-number">Vehicle no. {displayValue(operations.trip.vehicleNumber)}</div>
						)}
						{displayValue(operations.trip?.tripCounter ?? operations.trip?.trip_counter) !== "" && (
							<div className="dock-trip-number">TRIP ID: {formatTripCounter(displayValue(operations.trip?.tripCounter ?? operations.trip?.trip_counter))}</div>
						)}
						{operations.trip.allocatedBay !== "" && (
							<div className="dock-trip-number">Allocated bay: {operations.trip.allocatedBay}</div>
						)}
						<span>Complete checkpoint and milestone history</span>
					</div>
				<span className={`dock-status dock-status-${String(operations.trip?.tripStatus || "completed").toLowerCase()}`}>{operations.trip?.tripStatus}</span>
			</div>

			{/*
			 * Lifecycle timestamps, shown apart from the movement timeline below. These are business
			 * times, not observations: only an actual final Gate Out ever adds a movement, so nothing
			 * here draws one. `tripOver` above is unchanged and remains the only completion signal.
			 */}
			{!operations.trip?.tripOver && operations.trip?.dockLifecycle && (
				<div className="dock-trip-lifecycle">
					<span className={`dock-status ${lifecycleBadgeClass(operations.trip.dockLifecycle)}`}>{lifecycleBadgeLabel(operations.trip.dockLifecycle)}</span>
					<span className="dock-trip-lifecycle-summary">{lifecycleSummary(operations.trip.dockLifecycle, response.timezone)}</span>
					<span className="dock-trip-lifecycle-times">
						<em>Left first bay</em><b>{formatPlantTime(operations.trip.dockLifecycle.firstBayDepartedAt, response.timezone)}</b>
						{/* <em>Provisional status starts</em><b>{formatPlantTime(operations.trip.dockLifecycle.provisionalAt, response.timezone)}</b> */}
						<em>Return window ends</em><b>{formatPlantTime(operations.trip.dockLifecycle.returnUntil, response.timezone)}</b>
						{operations.trip.dockLifecycle.finalizedAt && <><em>Closed</em><b>{formatPlantTime(operations.trip.dockLifecycle.finalizedAt, response.timezone)}</b></>}
					</span>
				</div>
			)}

			{segments.length ? (
				<div className="dock-ribbon-section">
					<div className="dock-ribbon-legend">
						{layout === "ribbon" && kinds.map((kind) => <span key={kind.key}><i className={`dock-ribbon-swatch ${kind.key}`} />{kind.label}</span>)}
						<strong>Total {formatDuration(totalDuration)}</strong>
					</div>
					{layout === "timeline" && <div className={`dock-timeline-endpoint${segments[0]?.hideFromUi ? " dock-gate-undetected" : ""}`}>{shownTime(segments[0], segments[0].from, response.timezone)}</div>}
					<div className={`dock-ribbon-track${layout !== "timeline" ? " dock-ribbon-grouped" : ""}`}>
						{segments.map((segment, index) => {
							if (layout === "vertical") return <RailStage key={segment.key} segment={segment} trip={operations.trip} timezone={response.timezone} index={index} active={!operations.trip?.tripOver && index === segments.length - 1 && segment.eventAt == null} />;
							const duration = Math.max(0, segment.to - segment.from);
							/**
							 * Square-root scaling keeps short visits and transit readable alongside long Bay stays.
							 * Minimum widths also leave room for each visit's nested event labels.
							 */
							if (layout === "timeline") {
								const showDuration = segment.eventAt == null && Math.round(duration / 1000) > 0;
								return <div className={`dock-segment-entry${segment.kind === "transit" ? " dock-segment-transit" : ""}`} key={segment.key}>
									{segment.kind === "transit" && <KeyboardDoubleArrowDownIcon className="dock-transit-marker" aria-hidden="true" />}
									<details className="dock-segment-accordion">
										<summary>{segment.label}<span className="dock-segment-chevron" aria-hidden="true" /></summary>
										<span className="dock-segment-fields">
										<strong>{segment.label}</strong>
										<span><em>Location</em><b>{segment.location}</b></span>
										{segment.eventAt == null ? (
											<>
												<span><em>From</em><b>{formatEventTime(segment.from, response.timezone)}</b></span>
												<span><em>To</em><b>{formatEventTime(segment.to, response.timezone)}</b></span>
												{Math.round(duration / 1000) > 0 && <span><em>Duration</em><b>{formatDuration(duration)}</b></span>}
											</>
										) : <span><em>Time</em><b className={segment.hideFromUi ? "dock-gate-undetected" : undefined}>{shownTime(segment, segment.eventAt, response.timezone)}</b></span>}
										{segment.observation?.quantity != null && <span><em>Quantity</em><b>{segment.observation.quantity} {segment.observation.unit || "units"}</b></span>}
										{segment.source?.origin && <span><em>Source</em><b>{milestoneLabel(segment.source.origin)}</b></span>}
										{segment.evidence && <span><em>Evidence</em><b><CameraAltOutlinedIcon /> Recorded</b></span>}
									</span>
									</details>
									<VisitEvents events={segment.events} location={segment.location} timezone={response.timezone} />
									{showDuration && <span className="dock-segment-duration" aria-label={`Duration: ${formatDuration(duration)}`}>{formatDuration(duration)}</span>}
								</div>;
							}
							const growth = segment.eventAt == null ? Math.max(1, Math.sqrt(duration)) : 1;
							return (
								<div className={`dock-visit-group dock-visit-${segment.kind}${segment.events?.length ? " dock-visit-with-events" : ""}`} key={segment.key} style={layout === "vertical" ? undefined : { flexGrow: growth, minWidth: segment.events?.length ? `${segment.events.length * 110}px` : "80px" }}>
								{layout === "vertical" && <span className="dock-rail-node" aria-hidden="true">{segment.kind === "transit" && <LocalShippingIcon />}</span>}
								<RibbonSegment
									inline={layout === "vertical"}
									className={`dock-ribbon-segment ${segment.kind}`}
									aria-label={segment.eventAt == null ? `${segment.label}, ${formatDuration(duration)}` : `${segment.label}, ${shownTime(segment, segment.eventAt, response.timezone)}`}
								>
									<span className="dock-ribbon-label">
										{segment.label}{segment.hideFromUi && <small className="dock-gate-undetected"> · {UNDETECTED_GATE}</small>}
									</span>
									<span className="dock-ribbon-popup">
										<strong>{segment.label}</strong>
										<span><em>Location</em><b>{segment.location}</b></span>
										{segment.eventAt == null ? (
											<>
												<span><em>From</em><b>{formatEventTime(segment.from, response.timezone)}</b></span>
												<span><em>To</em><b>{formatEventTime(segment.to, response.timezone)}</b></span>
												{Math.round(duration / 1000) > 0 && <span><em>Duration</em><b>{formatDuration(duration)}</b></span>}
											</>
										) : <span><em>Time</em><b className={segment.hideFromUi ? "dock-gate-undetected" : undefined}>{shownTime(segment, segment.eventAt, response.timezone)}</b></span>}
										{segment.observation?.quantity != null && <span><em>Quantity</em><b>{segment.observation.quantity} {segment.observation.unit || "units"}</b></span>}
										{segment.source?.origin && <span><em>Source</em><b>{milestoneLabel(segment.source.origin)}</b></span>}
										{segment.evidence && <span><em>Evidence</em><b><CameraAltOutlinedIcon /> Recorded</b></span>}
									</span>
								</RibbonSegment>
								<VisitEvents events={segment.events} location={segment.location} timezone={response.timezone} inline={layout === "vertical"} />
								</div>
							);
						})}
					</div>
					{layout === "timeline"
						? <div className="dock-timeline-endpoint">{formatEventTime(timelineEnd, response.timezone)}</div>
						: layout === "vertical" ? null
						: <div className="dock-ribbon-axis"><span className={segments[0]?.hideFromUi ? "dock-gate-undetected" : undefined}>{shownTime(segments[0], segments[0].from, response.timezone)}</span><span>{formatEventTime(timelineEnd, response.timezone)}</span></div>}
				</div>
			) : <div className="dock-empty">No trip operations were recorded.</div>}
		</section>
	);
}
